import { ALPHA_2_ISO_3166, LIVING_SITUATIONS } from 'cpool-constants';
import type { ApplicantCreation, Schema_Wohnsituation } from 'cpool-schemas';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextInput } from 'cpool-components/input/TextInput';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { getFormErrorMessage } from '~/features/leadDetails/components/Overview/CustomerData/shared/getFormErrorMessage';
import { useIsSameHousehold } from '../../util/hooks/useIsSameHousehold';
import type { CreateApplicantFormValues } from '../../util/schemaCreateApplicantForm';
import { SameHouseholdSelector } from './SameHouseholdSelector';

type Props = {
    mainApplicantWohnsituation: Schema_Wohnsituation;
    savedWohnsituation: CreateApplicantFormValues['wohnsituation'] | null;
    onSaveWohnsituationValues: (values: CreateApplicantFormValues['wohnsituation']) => void;
};

export const Address: React.FC<Props> = ({
    mainApplicantWohnsituation,
    onSaveWohnsituationValues,
    savedWohnsituation,
}) => {
    const { t } = useTranslation();

    const {
        control,
        formState: { errors },
        register,
    } = useFormContext<ApplicantCreation>();

    const countryOptions = Object.values(ALPHA_2_ISO_3166).map((value) => ({
        label: t(`countries.${value}`),
        value,
    }));

    const livingTypeOptions = Object.values(LIVING_SITUATIONS).map((value) => ({
        label: t(`LIVING_SITUATION_DATA.LIVING_TYPE.${value}`),
        value,
    }));

    const isSameHousehold = useIsSameHousehold();

    return (
        <>
            <FormCard title={t('LIVING_SITUATION_DATA.ADDRESS')}>
                <SameHouseholdSelector
                    mainApplicantWohnsituation={mainApplicantWohnsituation}
                    savedWohnsituation={savedWohnsituation}
                    onSaveWohnsituationValues={onSaveWohnsituationValues}
                />

                <FormDropdown
                    id="wohnsituation.wohnart"
                    label={t('LIVING_SITUATION_DATA.LIVING_TYPE.TITLE')}
                    options={livingTypeOptions}
                    control={control}
                />

                <div className="flex w-full items-start justify-between gap-2.5">
                    <label className="mt-1.5 w-[175px] shrink-0 text-left text-sm text-midnight">
                        {t('ADDRESS.STREET')}, {t('ADDRESS.HOUSE_NUMBER')}
                    </label>
                    <div className="flex w-3/4 flex-col">
                        <TextInput
                            id="wohnsituation.anschrift.strasse"
                            disabled={isSameHousehold}
                            error={getFormErrorMessage(errors, 'wohnsituation.anschrift.strasse')}
                            {...register('wohnsituation.anschrift.strasse')}
                        />
                    </div>
                    <div className="flex w-1/4 flex-col">
                        <TextInput
                            id="wohnsituation.anschrift.hausnummer"
                            disabled={isSameHousehold}
                            error={getFormErrorMessage(errors, 'wohnsituation.anschrift.hausnummer')}
                            {...register('wohnsituation.anschrift.hausnummer')}
                        />
                    </div>
                </div>

                <div className="flex w-full items-start justify-between gap-2.5">
                    <label className="mt-1.5 w-[175px] shrink-0 text-left text-sm text-midnight">
                        {t('ADDRESS.ZIP_CODE_SHORT')}, {t('ADDRESS.LOCATION')}
                    </label>
                    <div className="flex w-1/4 flex-col">
                        <TextInput
                            id="wohnsituation.anschrift.plz"
                            disabled={isSameHousehold}
                            error={getFormErrorMessage(errors, 'wohnsituation.anschrift.plz')}
                            {...register('wohnsituation.anschrift.plz')}
                        />
                    </div>
                    <div className="flex w-3/4 flex-col">
                        <TextInput
                            id="wohnsituation.anschrift.ort"
                            disabled={isSameHousehold}
                            error={getFormErrorMessage(errors, 'wohnsituation.anschrift.ort')}
                            {...register('wohnsituation.anschrift.ort')}
                        />
                    </div>
                </div>

                <FormDropdown
                    id="wohnsituation.anschrift.land"
                    label={t('ADDRESS.COUNTRY')}
                    options={countryOptions}
                    control={control}
                    disabled={isSameHousehold}
                />

                <FormNumberInput
                    id="wohnsituation.anzahlPersonenImHaushalt"
                    label={t('LIVING_SITUATION_DATA.HAUSHALT.PERSONS_IN_HOUSEHOLD')}
                    control={control}
                    min={1}
                    allowDecimals={false}
                    disabled={isSameHousehold}
                />
            </FormCard>

            <FormCard>
                <FormDateInput
                    id="wohnsituation.anschrift.wohnhaftSeit"
                    label={t('ADDRESS.RESIDING_SINCE')}
                    control={control}
                />
            </FormCard>

            <FormCard title={t('LIVING_SITUATION_DATA.PREVIOUS_ADDRESS')}>
                <div className="flex w-full items-start justify-between gap-2.5">
                    <label className="mt-1.5 w-[175px] shrink-0 text-left text-sm text-midnight">
                        {t('ADDRESS.STREET')}, {t('ADDRESS.HOUSE_NUMBER')}
                    </label>
                    <div className="flex w-3/4 flex-col">
                        <TextInput
                            id="wohnsituation.vorherigeAnschrift.strasse"
                            error={getFormErrorMessage(errors, 'wohnsituation.vorherigeAnschrift.strasse')}
                            {...register('wohnsituation.vorherigeAnschrift.strasse')}
                        />
                    </div>
                    <div className="flex w-1/4 flex-col">
                        <TextInput
                            id="wohnsituation.vorherigeAnschrift.hausnummer"
                            error={getFormErrorMessage(errors, 'wohnsituation.vorherigeAnschrift.hausnummer')}
                            {...register('wohnsituation.vorherigeAnschrift.hausnummer')}
                        />
                    </div>
                </div>

                <div className="flex w-full items-start justify-between gap-2.5">
                    <label className="mt-1.5 w-[175px] shrink-0 text-left text-sm text-midnight">
                        {t('ADDRESS.ZIP_CODE_SHORT')}, {t('ADDRESS.LOCATION')}
                    </label>
                    <div className="flex w-1/4 flex-col">
                        <TextInput
                            id="wohnsituation.vorherigeAnschrift.plz"
                            error={getFormErrorMessage(errors, 'wohnsituation.vorherigeAnschrift.plz')}
                            {...register('wohnsituation.vorherigeAnschrift.plz')}
                        />
                    </div>
                    <div className="flex w-3/4 flex-col">
                        <TextInput
                            id="wohnsituation.vorherigeAnschrift.ort"
                            error={getFormErrorMessage(errors, 'wohnsituation.vorherigeAnschrift.ort')}
                            {...register('wohnsituation.vorherigeAnschrift.ort')}
                        />
                    </div>
                </div>

                <FormDropdown
                    id="wohnsituation.vorherigeAnschrift.land"
                    label={t('ADDRESS.COUNTRY')}
                    options={countryOptions}
                    control={control}
                />

                <FormDateInput
                    id="wohnsituation.vorherigeAnschrift.wohnhaftSeit"
                    label={t('ADDRESS.RESIDING_SINCE')}
                    control={control}
                />
            </FormCard>
        </>
    );
};
